import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';

import { LogService } from '../../../core/log/log.service';
import { selectInFlightDialogs } from '../../../core/current-page/current-page.selectors';
import { showSnackbar } from '../../../core/notifications/notification.actions';
import { NotificationService } from '../../../core/notifications/notification.service';
import { UserService } from '../../../domain/user/user.service';
import { selectUser } from '../../../domain/user/user.selectors';
import { EmailConfirmStatus } from '../../../domain/user/user.model';
import { ErrorMessage } from '../../../domain/error/error-message.model';
import { closeDialog, openDialog } from '../dn-dialog.actions';
import { MailConfirmDialogComponent } from './mail-confirm-dialog.component';
import {
  dismissEmailConfirmation,
  dismissEmailConfirmationSuccess,
  dismissEmailConfirmationFailure,
  openEmailConfirmDialog,
} from './mail-confirm-dialog.actions';

// Component name of the dialog this module opens. Duplicated as a string
// literal in the openDialog payload to survive minification and kept here so
// the dedupe filter below doesn't drift if the class is ever renamed.
const MAIL_CONFIRM_DIALOG_NAME = 'MailConfirmDialogComponent';

@Injectable({ providedIn: 'root' })
export class MailConfirmDialogEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private location: Location,
    private userService: UserService,
    private logService: LogService,
    private notificationService: NotificationService
  ) { }

  dismissEmailConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dismissEmailConfirmation),
      tap((action) =>
        this.logService.info('Effect: dismissEmailConfirmation', action)
      ),
      switchMap(() =>
        this.userService.dismissEmailConfirmation().pipe(
          map(() => dismissEmailConfirmationSuccess()),
          catchError((error: ErrorMessage) => of(dismissEmailConfirmationFailure({ error })))
        )
      )
    )
  );

  dismissEmailConfirmationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dismissEmailConfirmationSuccess),
      tap((action) =>
        this.logService.info('Effect: dismissEmailConfirmationSuccess', action)
      ),
      // Close the dialog. The site-access gate closure now flows via
      // `dialogClosedEvent → siteAccessGateClosed({gate:'mail-confirm'})`
      // (wired in site-access.effects.ts), so the content-render pipeline
      // observes the release through `selectSiteAccessCleared` rather than
      // through a dedicated containerOperationOk dispatch here.
      map(() => closeDialog())
    )
  );

  dismissEmailConfirmationFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dismissEmailConfirmationFailure),
      tap((action) =>
        this.logService.info('Effect: dismissEmailConfirmationFailure', action)
      ),
      map(({ error }) =>
        showSnackbar({
          payload: this.notificationService.getNotificationFromError(error),
        })
      )
    )
  );

  openEmailConfirmDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(openEmailConfirmDialog),
      tap((action) =>
        this.logService.info('Effect: openEmailConfirmDialog', action)
      ),
      concatLatestFrom(() => [
        this.store.select(selectUser),
        this.store.select(selectInFlightDialogs),
      ]),
      filter(([action, user, inFlight]) => {
        // Dedupe at source: if a MailConfirm dialog is already in flight
        // (dispatched but not yet closed), drop this emission. The
        // reducer updates `inFlightDialogs` synchronously on every
        // `openDialog` action — before the `onlyWhenSession` gate — so
        // this check is race-safe.
        if (inFlight.includes(MAIL_CONFIRM_DIALOG_NAME)) return false;
        // When triggered with explicit status (e.g. LinkExpired from
        // activateUserFailure$), skip the user status check — the user
        // may be a guest without a loaded profile.
        if (action.emailConfirmationStatus) return true;
        return (
          user.emailConfirmationStatus === EmailConfirmStatus.ShowFreeBanner ||
          user.emailConfirmationStatus === EmailConfirmStatus.ShowLockingBanner
        );
      }),
      concatMap(([action, user]) => {
        const status = action.emailConfirmationStatus || user.emailConfirmationStatus;
        const isLocking = status === EmailConfirmStatus.ShowLockingBanner;
        // Snapshot the URL the user is on right now so the activation-mail
        // link returns them to the same page instead of defaulting to home.
        // `Location.path(true)` instead of `Router.url` because on direct
        // landings (e.g. refresh on `/articolo/123` with mail-confirm pending)
        // the Router hasn't completed its initial navigation yet and
        // `Router.url` would still be `/`.
        return [
          openDialog({
            content: undefined,
            urlToDownload: undefined,
            urlWithContext: undefined,
            componentType: MailConfirmDialogComponent,
            componentName: MAIL_CONFIRM_DIALOG_NAME,
            disableClose: isLocking,
            panelClass: 'dn-dialog-small',
            data: {
              callbackUrl: action.callbackUrl || this.location.path(true),
              emailConfirmationStatus: status,
            },
          }),
        ];
      })
    )
  );
}
